/**
 * Trovva — Trial Banner
 *
 * Sticky banner shown at the top when org is on free trial.
 * Shows days remaining and upgrade CTA.
 * Hidden when trial is over (redirect to billing) or when on paid plan.
 */
import { useState } from 'react';
import { Clock, X, Zap } from 'lucide-react';
import { theme } from '../../lib/theme';

export default function TrialBanner({ org, onUpgrade }) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const plan = org?.billing_plan || 'trial';
  if (plan !== 'trial') return null;

  const trialEndsAt = org?.trial_ends_at ? new Date(org.trial_ends_at) : null;
  if (!trialEndsAt) return null;

  const now = new Date();
  const daysLeft = Math.max(0, Math.ceil((trialEndsAt - now) / (1000 * 60 * 60 * 24)));
  const isExpired = daysLeft <= 0;
  const isUrgent = daysLeft <= 3;

  const bgColor = isExpired ? '#dc2626' : isUrgent ? '#f59e0b' : theme.colors.primary;
  const textColor = '#ffffff';

  return (
    <div style={{
      background: bgColor,
      color: textColor,
      padding: '8px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      fontSize: 13,
      fontWeight: 500,
      position: 'relative',
      zIndex: 1000,
    }}>
      <Clock size={14} />
      <span>
        {isExpired
          ? 'Your free trial has ended. Upgrade to continue using Trovva.'
          : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left in your free trial.`
        }
      </span>
      <button
        onClick={onUpgrade}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 12px',
          borderRadius: 6,
          border: '1px solid rgba(255,255,255,0.4)',
          background: 'rgba(255,255,255,0.15)',
          color: textColor,
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        <Zap size={12} />
        Upgrade Now
      </button>
      {!isExpired && (
        <button
          onClick={() => setDismissed(true)}
          style={{
            position: 'absolute',
            right: 12,
            background: 'none',
            border: 'none',
            color: textColor,
            cursor: 'pointer',
            opacity: 0.7,
            padding: 4,
          }}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
